import React from "react";
import { motion } from "framer-motion";
import { all_links } from "./animation.config";

// --- Reusable Constants ---
const futuristicClipPath = {
  clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)",
};

// --- Sub-components for Polish and Reusability ---

/**
 * Renders a star rating display with support for full, half, and empty stars.
 * This provides a much more polished look than simply coloring full stars.
 */
const StarRating = ({ rating = 0, maxRating = 5 }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.4; // Use a threshold for rounding
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  // Use a unique ID for the gradient to prevent conflicts if this component is used multiple times.
  const gradientId = React.useId();

  return (
    <div className="flex items-center">
      {/* Full Stars */}
      {[...Array(fullStars)].map((_, i) => (
        <svg key={`full-${i}`} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {/* Half Star */}
      {hasHalfStar && (
        <svg className="w-4 h-4 text-gray-500" fill={`url(#${gradientId})`} viewBox="0 0 20 20">
          <defs>
            <linearGradient id={gradientId}>
              <stop offset="50%" stopColor="#facc15" /> {/* yellow-400 */}
              <stop offset="50%" stopColor="currentColor" />
            </linearGradient>
          </defs>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
      {/* Empty Stars */}
      {[...Array(emptyStars)].map((_, i) => (
        <svg key={`empty-${i}`} className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

/**
 * A reusable card for displaying ratings, featuring a dynamic hover glow.
 */
const RatingCard = ({ iconSrc, alt, rating, sourceText, href, glowColor }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="no-underline group"
    style={{ "--glow-color": glowColor }}
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    }}
  >
    <div className="flex items-center gap-4 px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-300 ease-in-out hover:border-white/40 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[var(--glow-color)]/30">
      <motion.div 
        className="w-12 h-12 rounded-lg flex-shrink-0"
        whileHover={{ scale: 1.15, rotate: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <img src={iconSrc} alt={alt} className="w-full h-full object-contain" />
      </motion.div>
      <div className="flex flex-col">
        <div className="flex items-baseline gap-1">
          <span className="text-white font-bold text-lg">{rating.toFixed(1)}</span>
          <span className="text-white/70 text-sm">/ 5.0</span>
          <div className="ml-2">
            <StarRating rating={rating} />
          </div>
        </div>
        <span className="text-white/80 text-sm">{sourceText}</span>
      </div>
    </div>
  </motion.a>
);

/**
 * Secondary button with a futuristic gradient border and clipped corner.
 */
const BorderGradientButton = ({ children, href, gradient }) => (
  <motion.a
    href={href}
    className="group relative inline-block"
    variants={{
      hidden: { opacity: 0, scale: 0.8 },
      visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
    }}
    whileHover={{ y: -2, scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    transition={{ type: "spring", stiffness: 400, damping: 15 }}
  >
    <div
      className="relative"
      style={{
        ...futuristicClipPath,
        padding: "2px",
        backgroundImage: gradient,
      }}
    >
      <div
        className="flex items-center justify-center bg-black/80 backdrop-blur-sm px-5 py-2 transition-colors duration-300 ease-in-out group-hover:bg-black/60"
        style={futuristicClipPath}
      >
        <span className="text-sm md:text-base font-bold text-cyan-200 group-hover:text-white transition-colors duration-300">
          {children}
        </span>
      </div>
    </div>
  </motion.a>
);

/**
 * The primary call-to-action button with a premium shimmer effect on hover.
 */
const PrimaryButton = ({ children, href }) => (
  <motion.a
    href={href}
    className="group relative inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm md:text-base font-bold text-white 
               bg-gradient-to-r from-cyan-500 to-blue-600 overflow-hidden
               transition-shadow duration-300 ease-in-out 
               shadow-lg shadow-cyan-500/20 hover:shadow-2xl hover:shadow-cyan-500/40"
    variants={{
      hidden: { opacity: 0, scale: 0.8 },
      visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
    }}
    whileHover={{ y: -4, scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    transition={{ type: "spring", stiffness: 400, damping: 15 }}
  >
    {/* Shimmer Effect */}
    <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent 
                     transform -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
    <span className="relative z-10">{children}</span>
  </motion.a>
);

/**
 * Main component that assembles the action buttons and feature cards.
 */
export const ActionButtons = () => {
  const ratingCards = [
    {
      iconSrc: "/assets/icons/chrome.svg",
      alt: "Chrome",
      rating: 4.7,
      sourceText: "On Chrome Store",
      href: all_links.all.chrome_extension,
      glowColor: "#22d3ee", // cyan-400
    },
    {
      iconSrc: "/assets/icons/trust-pilot.png",
      alt: "Trustpilot",
      rating: 4.0,
      sourceText: "On Trustpilot",
      href: all_links.trustpilot,
      glowColor: "#4ade80", // green-400
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };
  
  return (
    <div className="absolute bottom-8 left-0 w-full px-4 md:px-8 pointer-events-auto">
      <motion.div
        className="flex flex-col items-center justify-center gap-8"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Action Buttons Group */}
        <motion.div
          className="flex flex-col sm:flex-row justify-center items-center gap-4"
          variants={containerVariants}
        >
          <BorderGradientButton
            href={all_links.free_trial}
            gradient="linear-gradient(to right, #22d3ee, #4f46e5)"
          >
            Free Trial
          </BorderGradientButton>

          <PrimaryButton href={all_links.account}>
            My Account
          </PrimaryButton>

          <BorderGradientButton
            href={all_links.extension}
            gradient="linear-gradient(135deg, #7c3aed, #0891b2)"
          >
            Extension
          </BorderGradientButton>
        </motion.div>

        {/* Feature Cards Group */}
        <motion.div
          className="flex flex-col pb-5 md:flex-row justify-center items-center gap-4 md:gap-6"
          variants={containerVariants}
        >
          {ratingCards.map((card) => (
            <RatingCard key={card.sourceText} {...card} />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};